import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { easyWebinarLogoLight } from '../../../../assets/images';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { AI_AUTH_COLORS } from '../constants';
import AuthService from '../../../../services/auth.service';
import { getAilaunchRedirectUrl } from '../../../../utils/redirect.util';

const CODE_LENGTH = 6;

export interface TryAiFlowEmailVerificationLocationState {
  email: string;
  password: string;
  companyId: string;
  companyName: string;
}

export default function TryAiFlowEmailVerificationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const state = location.state as TryAiFlowEmailVerificationLocationState | null;
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const email = useMemo(
    () => state?.email ?? searchParams.get('email')?.trim() ?? '',
    [state?.email, searchParams]
  );
  const trimmedCode = useMemo(() => code.trim(), [code]);
  const isVerifyDisabled = trimmedCode.length < CODE_LENGTH || isLoading;

  useEffect(() => {
    if (!email) {
      navigate('/try-ai-flow/auth/create-password', { replace: true });
      return;
    }
    if (!state) {
      navigate(`/try-ai-flow/auth/create-password?email=${encodeURIComponent(email)}`, {
        replace: true,
      });
    }
  }, [email, state, navigate]);

  const handleCodeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setCode(event.target.value);
  }, []);

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!state || trimmedCode.length < CODE_LENGTH || isLoading) return;
      setIsLoading(true);
      try {
        await AuthService.confirmSignup({
          email: state.email,
          confirmationCode: trimmedCode,
          password: state.password,
          companyId: state.companyId,
          companyName: state.companyName,
          infLeadSquaredId: '',
        });
        toast.success('Account verified.');
        const isLogin = searchParams.get('isLogin') === 'true';
        if (isLogin) {
          window.location.href = getAilaunchRedirectUrl() + '&isLogin=true';
        } else {
          window.location.href = getAilaunchRedirectUrl();
        }
      } catch (err: any) {
        toast.error(err?.message ?? 'Invalid code or confirmation failed.');
      } finally {
        setIsLoading(false);
      }
    },
    [state, trimmedCode, isLoading]
  );

  const handleResendCode = useCallback(async () => {
    if (!state?.email || resendLoading) return;
    setResendLoading(true);
    try {
      await AuthService.resendConfirmationCode(state.email);
      toast.success('Confirmation code sent again. Check your email.');
    } catch (err: any) {
      toast.error(err?.message ?? 'Failed to resend code.');
    } finally {
      setResendLoading(false);
    }
  }, [state?.email, resendLoading]);

  const handleBack = useCallback(() => {
    navigate(`/try-ai-flow/auth/create-password?email=${encodeURIComponent(email)}`);
  }, [email, navigate]);

  if (!state?.email) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        bgcolor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Link to="/try-ai-flow" style={{ display: 'inline-flex', alignItems: 'center' }}>
          <img
            src={easyWebinarLogoLight}
            alt="EasyWebinar"
            style={{ display: 'block', height: 30, width: 'auto' }}
          />
        </Link>
        <Button
          component={Link}
          to="/try-ai-flow/auth/login"
          variant="contained"
          endIcon={<ChevronRight size={16} />}
          sx={{
            textTransform: 'none',
            borderRadius: '999px',
            px: 1.9,
            py: 0.7,
            fontSize: '0.85rem',
            fontWeight: 600,
            color: '#111827',
            bgcolor: '#FFFFFF',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#F9FAFB',
              boxShadow: 'none',
            },
          }}
        >
          Login
        </Button>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: 4,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          <Typography
            component="h1"
            sx={{
              textAlign: 'center',
              color: '#111827',
              fontWeight: 600,
              fontSize: { xs: '2rem', sm: '2.2rem' },
              letterSpacing: '-0.01em',
              mb: 0.7,
            }}
          >
            Check your email
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              color: '#6B7280',
              fontSize: '0.9rem',
              lineHeight: 1.45,
              mb: 2.5,
            }}
          >
            Enter the verification code we just sent to
            <br />
            <Box component="span" sx={{ color: '#111827' }}>
              {email || 'your email'}
            </Box>
          </Typography>

          <Box component="form" onSubmit={handleSubmit} autoComplete="off">
            <TextField
              fullWidth
              value={code}
              onChange={handleCodeChange}
              placeholder="Enter 6-digit code"
              inputProps={{ maxLength: CODE_LENGTH, autoComplete: 'off' }}
              variant="outlined"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  bgcolor: '#FFFFFF',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#D1D5DB' },
                  '&.Mui-focused fieldset': { borderColor: AI_AUTH_COLORS.primaryBorder },
                },
                '& input': {
                  fontSize: '0.88rem',
                },
                mb: 1.4,
              }}
            />

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={isVerifyDisabled}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                py: 1.05,
                fontSize: '0.9rem',
                fontWeight: 600,
                color: '#FFFFFF',
                bgcolor: AI_AUTH_COLORS.primaryMain,
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: AI_AUTH_COLORS.primaryHover,
                  boxShadow: 'none',
                },
                '&.Mui-disabled': {
                  bgcolor: AI_AUTH_COLORS.primaryDisabled,
                  color: '#FFFFFF',
                },
              }}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={20} sx={{ color: '#FFFFFF', mr: 1 }} />
                  Verifying…
                </>
              ) : (
                'Verify & Continue'
              )}
            </Button>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 1.4,
            }}
          >
            <Typography
              component="button"
              type="button"
              onClick={handleResendCode}
              disabled={resendLoading}
              sx={{
                border: 0,
                background: 'transparent',
                p: 0,
                m: 0,
                fontSize: '0.84rem',
                color: AI_AUTH_COLORS.primary,
                fontWeight: 600,
                cursor: resendLoading ? 'not-allowed' : 'pointer',
                textDecoration: 'none',
                opacity: resendLoading ? 0.7 : 1,
                '&:hover': { textDecoration: resendLoading ? 'none' : 'underline' },
              }}
            >
              {resendLoading ? 'Sending…' : 'Resend code'}
            </Typography>
            <Typography
              component="button"
              type="button"
              onClick={handleBack}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                border: 0,
                background: 'transparent',
                p: 0,
                m: 0,
                fontSize: '0.82rem',
                color: '#6B7280',
                cursor: 'pointer',
                '&:hover': {
                  color: '#000000',
                },
              }}
            >
              <ArrowLeft size={16} /> Back
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
